import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

export const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('usuario');

    if (!token || !savedUser) {
      setLoading(false);
      return;
    }

    // A cópia guardada aparece PRIMEIRO, para a tela não piscar em branco
    // esperando a rede.
    try {
      setUsuario(JSON.parse(savedUser));
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      setLoading(false);
      return;
    }
    setLoading(false);

    /*
      E logo depois ela é REVALIDADA contra o servidor.

      Até 21/08/2026 isto não existia: o `localStorage` era lido e nunca mais
      conferido, então tudo o que veio no login ficava congelado por até oito
      horas — o tempo do token, que não tem renovação. Na prática:

        - trocar o perfil de alguém não mudava o menu dele até relogar;
        - corrigir o nome da central não chegava a ninguém;
        - permissão retirada continuava desenhando o botão.

      O servidor sempre conferiu de verdade a cada requisição, então isso nunca
      foi buraco de segurança — era tela mostrando o que já não vale, que é
      pior de diagnosticar do que erro, porque não parece defeito.

      Falha de rede NÃO desloga: fica a cópia guardada. Quem está no meio de um
      atendimento não pode perder a sessão porque a API piscou. Só 401 desloga,
      e disso quem cuida é o interceptor do axios.
    */
    api.get('/auth/me')
      .then((r) => {
        const atual = r.data?.data;
        if (!atual) return;
        localStorage.setItem('usuario', JSON.stringify(atual));
        setUsuario(atual);
      })
      .catch(() => { /* sem rede: segue com a cópia guardada */ });
  }, []);

  const login = useCallback(async (usuarioInput, senha) => {
    const response = await api.post('/auth/login', {
      usuario: usuarioInput,
      senha,
    });

    const { token, usuario: userData } = response.data.data;

    localStorage.setItem('token', token);
    localStorage.setItem('usuario', JSON.stringify(userData));
    setUsuario(userData);

    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignorar erro no logout
    }

    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    setUsuario(null);
  }, []);

  const isAuthenticated = !!usuario;

  const hasRole = useCallback(
    (...roles) => {
      if (!usuario) return false;
      return roles.includes(usuario.nivel);
    },
    [usuario]
  );

  const hasPermission = useCallback(
    (modulo, acao) => {
      if (!usuario) return false;
      // Fallback: se nao tem permissoes no objeto, usar hasRole para admin
      if (!usuario.permissoes) {
        return usuario.nivel === 'admin';
      }
      const modPerms = usuario.permissoes[modulo];
      if (!modPerms) return false;
      return modPerms.includes(acao);
    },
    [usuario]
  );

  const canAccessModule = useCallback(
    (modulo) => hasPermission(modulo, 'ver'),
    [hasPermission]
  );

  return (
    <AuthContext.Provider
      value={{
        usuario,
        loading,
        isAuthenticated,
        login,
        logout,
        hasRole,
        hasPermission,
        canAccessModule,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
